import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import { PDVShortcut } from './PDVShortcut';
import { PDVThemeColors } from './PDVThemes';
import { pdvScale, MQ_PHONE } from './pdvScale';

interface PDVFooterProps {
  colors: PDVThemeColors;
  nomeCaixa?: string;
  nomeOperador?: string;
  // Duração "02h 45m" desde a abertura do turno (ver formatarDuracaoAberta
  // em Pdv.tsx) — exibida junto com o nome do Caixa.
  duracaoAberta?: string;
  // Horário "08:30" em que a sessão foi aberta — exibido junto com a duração.
  horaAbertura?: string;
  // Fechar Caixa não faz sentido com o caixa já fechado — fica desabilitado
  // até o operador abrir o turno (F2).
  caixaAberto: boolean;
  onFecharCaixa: () => void;
  onResumoVendas: () => void;
  // Notas fiscais: vendas sem NFC-e (emitir depois) e NFC-e emitidas
  // (reimprimir) — ver DialogNotasFiscaisPDV.
  onNotasFiscais: () => void;
  onConfiguracoes: () => void;
  // Tema saiu do header (que agora só tem logo + hora) e voltou pra cá.
  onTema: () => void;
  onSair: () => void;
}

// Mesma fórmula de luminância usada em Pdv.tsx (corEhClara) pra decidir a
// cor da logo do header — duplicada aqui porque não é exportada de lá.
const corEhClara = (hex?: string): boolean => {
  if (!hex) return false;
  const limpo = hex.replace('#', '');
  if (limpo.length !== 6) return false;
  const r = parseInt(limpo.slice(0, 2), 16);
  const g = parseInt(limpo.slice(2, 4), 16);
  const b = parseInt(limpo.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 128;
};

export const PDVFooter: React.FC<PDVFooterProps> = ({
  colors, nomeCaixa, nomeOperador, duracaoAberta, horaAbertura, caixaAberto, onFecharCaixa, onResumoVendas, onNotasFiscais, onConfiguracoes, onTema, onSair,
}) => {
  // Sem card/fundo próprios (a pedido) — só os ícones, colados na borda
  // inferior da tela, sobre o que estiver atrás (fundo geral do PDV).
  // Nome do Caixa/Operador, rótulos (CAIXA/OPERADOR) e a linha de Sessão
  // usam a cor de destaque do tema — mas em temas com fundo claro (siac,
  // azul, roxo, rosa, mercado, laranja, básico...), a cor de destaque quase
  // some sobre o fundo claro. Nesses casos usa primaryDark (tom mais escuro
  // do mesmo tema) em vez do solid. Só a opacidade muda entre
  // rótulo/nome/sessão pra manter a hierarquia (rótulo/sessão mais sutis
  // que o nome em si).
  //
  // "Tem foto de fundo" (fotografia de verdade, url(...) — sunset,
  // goldenriver, petroleo) sempre conta como fundo escuro/complexo,
  // sem olhar pro backgroundColor (que é só um fallback antes da foto
  // carregar, não representa o tom real da imagem). Temas mais novos com
  // backgroundImage 100% CSS (gradiente, sem foto — básico, ocean, grafite)
  // não caem nessa regra: a cor real vem do backgroundColor mesmo,
  // corretamente clara ou escura conforme o tema.
  const temFotoDeFundo = !!colors.backgroundImage?.includes('url(');
  const fundoPDVClaro = !temFotoDeFundo && corEhClara(colors.backgroundColor);
  // footerAccent — override por tema pra essa cor específica (ver
  // PDVThemes.ts) quando primarySolid não serve pro rodapé (ex.: grafite,
  // onde primarySolid também é usado como fundo de outro elemento e não dá
  // pra simplesmente clarear). Cai de volta em primarySolid quando ausente.
  const corNomes = fundoPDVClaro ? colors.primaryDark : (colors.footerAccent ?? colors.primarySolid);
  const corRotulo = alpha(corNomes, 0.75);
  const corSessao = alpha(corNomes, 0.7);

  return (
    <Box
      sx={{
        display: 'flex',
        // No celular os shortcuts (que já quebram linha sozinhos, ver
        // flexWrap mais abaixo) podem empurrar o rodapé pra 2 linhas —
        // altura fixa (pdvScale(80)) cortaria a 2ª linha, então vira auto.
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        rowGap: 0,
        gap: pdvScale(8),
        // 80px em 1920×1080 — participa da mesma escala global (--pdv-scale)
        // de tudo, ver pdvScale.ts.
        height: pdvScale(80),
        mx: pdvScale(32),
        // Espaço acima (folga entre o Subtotal, acima, e o nome do
        // Caixa/Operador aqui embaixo) — pequeno de propósito e na MESMA
        // escala de tudo: numa tela maior quem cresce é o tamanho dos
        // ícones/textos (ver PDVShortcut e os fontSize acima), não essa
        // distância entre os blocos da tela.
        mt: pdvScale(12),
        mb: 0,
        px: pdvScale(20),
        py: 0,
        position: 'relative',
        zIndex: 2,
        [MQ_PHONE]: {
          flexWrap: 'wrap',
          justifyContent: 'center',
          rowGap: '8px',
          height: 'auto',
          mx: '12px',
          mb: '8px',
          py: '8px',
        },
      }}
    >
      {/* Sem ícones — só o texto (a pedido). Sessão (duração desde a
          abertura) junto com o nome do Caixa, não mais um bloco à parte. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: pdvScale(24),
          minWidth: 0,
          flexShrink: 0,
          flexBasis: 'auto',
          [MQ_PHONE]: {
            justifyContent: 'center',
            flexBasis: '100%',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: pdvScale(12), fontWeight: 700, color: corRotulo, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
            Caixa
          </Typography>
          <Typography noWrap sx={{ fontSize: pdvScale(16), fontWeight: 700, color: corNomes, maxWidth: pdvScale(180) }}>
            {nomeCaixa || '—'}
          </Typography>
          {duracaoAberta && (
            <Typography noWrap sx={{ fontSize: pdvScale(11), fontWeight: 600, color: corSessao }}>
              Sessão: {duracaoAberta}{horaAbertura ? ` (aberta às ${horaAbertura})` : ''}
            </Typography>
          )}
        </Box>

        {nomeOperador && (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: pdvScale(12), fontWeight: 700, color: corRotulo, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
              Operador
            </Typography>
            <Typography noWrap sx={{ fontSize: pdvScale(16), fontWeight: 700, color: corNomes, maxWidth: pdvScale(160) }}>
              {nomeOperador}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: pdvScale(42),
          flexBasis: 'auto',
          [MQ_PHONE]: {
            justifyContent: 'center',
            gap: '18px',
            flexBasis: '100%',
          },
        }}
      >
        <PDVShortcut icon={PointOfSaleRoundedIcon} label="Fechar Caixa" shortcut="F7" onClick={onFecharCaixa} disabled={!caixaAberto} colors={colors} />
        <PDVShortcut icon={BarChartRoundedIcon} label="Resumo de Vendas" onClick={onResumoVendas} colors={colors} />
        <PDVShortcut icon={ReceiptLongRoundedIcon} label="Notas Fiscais" onClick={onNotasFiscais} colors={colors} />
        <PDVShortcut icon={SettingsRoundedIcon} label="Configurações" shortcut="F10" onClick={onConfiguracoes} colors={colors} />
        <PDVShortcut icon={PaletteRoundedIcon} label="Tema" onClick={onTema} colors={colors} />
        <PDVShortcut icon={PowerSettingsNewRoundedIcon} label="Sair" shortcut="ESC" onClick={onSair} colors={colors} />
      </Box>
    </Box>
  );
};
